import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Self-service onboarding. Creating an account bootstraps a brand-new organization
 * with the signing-up user as its owner/admin. Email/password only — Microsoft SSO
 * users join existing orgs via admin provisioning, not signup.
 */
export function SignupPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [organizationName, setOrganizationName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      navigate('/', { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await auth.signUp({ email, password, organizationName, displayName });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-purple to-brand-purple-light">
      <div className="w-full max-w-md rounded-xl bg-white p-10 shadow-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-brand-purple">PathChron</h1>
          <p className="text-sm text-gray-500">Enterprise Device Management</p>
        </div>

        <div className="mb-6 text-center">
          <h2 className="text-lg font-semibold text-brand-black">Create your organization</h2>
          <p className="mt-1 text-sm text-gray-400">
            Set up a new organization and your owner account
          </p>
        </div>

        {auth.error && (
          <div className="mb-4 rounded-lg bg-brand-orange-light p-3 text-sm text-brand-orange">
            {auth.error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="org-name" className="mb-1 block text-sm font-medium text-brand-black">
              Organization name
            </label>
            <input
              id="org-name"
              type="text"
              required
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
              placeholder="Acme Corporation"
            />
          </div>
          <div>
            <label htmlFor="display-name" className="mb-1 block text-sm font-medium text-brand-black">
              Your name
            </label>
            <input
              id="display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-brand-black">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-brand-black">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
              placeholder="At least 6 characters"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-3 rounded-lg bg-brand-purple px-6 py-3 font-medium text-white shadow-sm transition-colors hover:bg-brand-purple/90 disabled:opacity-50"
          >
            {submitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-purple hover:underline">
            Sign in
          </Link>
        </p>

        <p className="mt-8 text-center text-xs text-gray-400">
          PathChron — Enterprise Device Management
        </p>
      </div>
    </div>
  );
}
